// @givmo/org-console — ONE REQUEST: where a person confirms or declines what an AI agent asked for
// (MCP-P6a; floors design decision 4.1, and plan §2.4 item 1's three properties of a Givmo surface).
//
//   1. THE PERSON AUTHENTICATES TO GIVMO DIRECTLY. They reach this page signed in to the console, and
//      Confirm runs through `@givmo/step-up`'s gate: a `step_up_required` refusal opens the ONE
//      "Confirm it's you" sheet (D-AS-21, drawn by the host's provider, untouched here), and the
//      confirm is retried once. A `gat_` bearer never reaches this page at all.
//   2. GIVMO RENDERS THE REQUEST FROM THE SERVER'S RECORD. Everything in the card is a typed field of
//      Givmo's render (`org-request-model.ts`), wrapped in this page's own words; nothing an agent
//      wrote is drawn as Givmo's. A field this build cannot draw turns Confirm OFF (decision 4.4).
//   3. THE CONFIRMATION IS BOUND TO WHAT WAS SHOWN. Confirm posts the content hash of the render on
//      screen. A drifted request answers 409 with the fresh render: the page redraws it, marks the
//      line that changed, and asks again (`confirmation_request_changed`).
//
// On success it shows what happened. A card act whose charge may have gone through (MCP-P5f, cut
// A4) shows the payments estate's own words and keeps the request open: confirming again replays the
// same charge under the request's completion key, never a second one. The read carries that state
// (MCP-P5l), so a page drawn fresh says it too, with Decline off in the estate's words.
//
// A SESSION THE SERVER HOLDS TO THE EMAILED CODE'S LIMITS (Account Security's AS-PWL-2) is refused
// with the code that names its cure (`context.confirm_with`): `reauth` is answered by the sheet's
// own ways in, like `step_up_required`. `passkey` goes to the sheet's passkey arm and `add_passkey` to
// the host's first-passkey offer, in Confirm's place, Decline and Withdraw staying beside it in every
// state (MCP-P6d, `org-first-passkey.tsx`). On a host
// where no passkey can work neither can be answered, so the sheet says so in this page's words rather
// than prompt for a sign-in that would be refused again (W5).
//
// Against a backend without MCP-P6a's fields this page says less (no names on a confirmation, no
// "over your budget" figure, no "someone else must confirm" before a confirm meets the 409, no
// record of an ended request).

import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  ErrorMessage,
  Icon,
  PageHeader,
  Skeleton,
  shouldInterceptClick,
  testIdProp,
  type LucideLikeIcon,
} from '@givmo/design-system';
import { DisclosureTerms } from '@givmo/disclosure';
import { firstErrorContext, type GivmoError } from '@givmo/api-client';
import { isStepUpCancelled, useStepUpGate } from '@givmo/step-up';
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  CircleCheck,
  Info,
  TriangleAlert,
  UserRound,
} from 'lucide-react';
import type { OrgConsoleConfig } from './org-console-config';
import { isOrgConsoleUnavailable } from './org-console-transport';
import { useFirstPasskey } from './org-first-passkey';
import {
  ACT_ROW_COPY,
  ADD_PASSKEY_ROW,
  BANNER_COPY,
  FLOOR_CONTEXT_COPY,
  FLOOR_LOOSENING_ACTION,
  MONEY_CONFIRM_ACTION,
  OUTCOME_COPY,
  PASSKEY_ROW,
  PROPOSER_COPY,
  REAUTH_ROW,
  REQUEST_PAGE_COPY,
  VOID_REASON_COPY,
  WHY_COPY,
  requestStepUpCopy,
} from './org-request-copy';
import {
  changedFieldKeys,
  drawRequest,
  formatMinorUsd,
  formatRequestTime,
  proposedByOf,
  relativeTime,
  requestStateLabel,
  requestStateTone,
  viewerStanding,
  type DrawnRequest,
  type DrawnRow,
} from './org-request-model';
import {
  confirmOrgRequest,
  declineOrgRequest,
  fetchOrgRequest,
  isOrgRequestIneligible,
  isOrgRequestNeedsAnotherPerson,
  isOrgRequestNotPending,
  orgRequestChangedTo,
  orgRequestMaybeCharged,
  orgRequestPaymentSettling,
  withdrawOrgRequest,
} from './org-request-transport';
import type { OrgRequestWords } from './org-request-words';
import {
  decodeOrgRequestDetail,
  isOpenRequestState,
  type OrgRequestDetail,
  type OrgRequestRender,
} from './org-request-wire';
import { ORG_REQUESTS_QUERY_KEY, PageFrame } from './org-requests-page';

export interface OrgRequestPageProps {
  config: OrgConsoleConfig;
  /** The request's `cfr_` id, from the host's route. */
  requestId: string;
  /** The inbox's address, for the way back. */
  inboxHref: string;
  /** The host's in-app hop for a plain click. */
  onNavigate: (href: string) => void;
  /** Where a finished money act's record lives in the host (the Donations activity), if anywhere. */
  actHref?: (actRef: string) => string | null;
  /** The host's words for its own money surfaces, families, rails and recipients (R5). */
  words: OrgRequestWords;
}

const PREFIX = 'org-request';

/** What the page draws: the render on screen and the hash a confirm will post. */
interface Shown {
  render: OrgRequestRender;
  contentHash: string;
  /** Lines a drift 409 changed, and what they said before, for the highlight. */
  changed: { keys: string[]; before: DrawnRequest } | null;
}

/** What happened after an act on this page, held until the person leaves it. */
type Outcome =
  | { kind: 'completed'; drawn: DrawnRequest; detail: OrgRequestDetail }
  | { kind: 'declined'; drawn: DrawnRequest | null; detail: OrgRequestDetail }
  | { kind: 'first_of_two'; detail: OrgRequestDetail };

/**
 * The cure a refusal of a session held to the emailed code's limits names (AS-PWL-2:
 * `context.confirm_with`): `reauth`, `add_passkey` or `passkey`; null for any other refusal.
 */
export function confirmWithOf(error: GivmoError): string | null {
  const value = firstErrorContext(error)?.confirm_with;
  return typeof value === 'string' ? value : null;
}

export function OrgRequestPage({
  config,
  requestId,
  inboxHref,
  onNavigate,
  actHref,
  words,
}: OrgRequestPageProps) {
  const queryClient = useQueryClient();
  const queryKey = [ORG_REQUESTS_QUERY_KEY, config.orgRef, 'one', requestId];
  const detailQuery = useQuery({
    queryKey,
    queryFn: async ({ signal }) =>
      decodeOrgRequestDetail(await fetchOrgRequest(config.orgRef, requestId, { signal })),
    retry: (count, error) => !isOrgConsoleUnavailable(error) && count < 1,
  });

  const [drift, setDrift] = useState<Shown | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [maybeCharged, setMaybeCharged] = useState<string | null>(null);
  const [needsAnother, setNeedsAnother] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const detail = detailQuery.data ?? null;
  const shown: Shown | null = useMemo(() => {
    if (drift !== null) return drift;
    if (detail?.render && detail.contentHash) {
      return { render: detail.render, contentHash: detail.contentHash, changed: null };
    }
    return null;
  }, [detail, drift]);
  const drawn = shown !== null ? drawRequest(shown.render, words) : null;

  // The sheet's words for THIS request (the act in the consent screen's shape).
  const stepUpWhat =
    drawn === null
      ? 'confirm this request'
      : drawn.act
        ? drawn.act
        : drawn.kind === 'money'
          ? `give ${drawn.outcome.amount} to ${drawn.outcome.recipient} from your ${drawn.outcome.source}`
          : drawn.hero !== null
            ? `${drawn.title.charAt(0).toLowerCase()}${drawn.title.slice(1)} to ${drawn.hero.to}`
            : `${drawn.title.charAt(0).toLowerCase()}${drawn.title.slice(1)}`;
  const stepUpKind = drawn?.kind === 'money' ? 'money' : 'floor';
  // The sheet's row is keyed by the act the server names (its `context.action`, the read's
  // `step_up.action`), so a surface's own loosening action finds these words too.
  const stepUpAction =
    detail?.stepUp?.action ??
    (stepUpKind === 'money' ? MONEY_CONFIRM_ACTION : FLOOR_LOOSENING_ACTION);
  const firstPasskey = useFirstPasskey();
  const gate = useStepUpGate({
    copy: requestStepUpCopy(stepUpWhat, stepUpKind, stepUpAction, firstPasskey.worksHere),
    testIdPrefix: `${PREFIX}-step-up`,
    // W5 (AS-PWL-2e): a session held to the emailed code's limits is refused with its cure named
    // (`context.confirm_with`), and each cure opens its own row. `reauth` takes the sheet's ways in.
    // `passkey` is answered by the passkey alone, which the sheet's arm runs where a passkey can work
    // (MCP-P6d); elsewhere the row says it can't. `add_passkey` needs a passkey set up first: where one
    // can be, only a passkey answers, so an account holding none fails the gate with the typed reason
    // and meets the offer (`useFirstPasskey`); where none can be, the sheet says so instead of asking
    // for a sign-in that would be refused again.
    refusalCodes: {
      passkey_setup_required: (error) =>
        confirmWithOf(error) === 'add_passkey'
          ? { row: ADD_PASSKEY_ROW, accepts: firstPasskey.worksHere ? ['passkey'] : [] }
          : confirmWithOf(error) === 'reauth'
            ? { row: REAUTH_ROW }
            : undefined,
      passkey_confirmation_required: (error) =>
        confirmWithOf(error) === 'passkey' ? { row: PASSKEY_ROW } : undefined,
    },
  });

  const refresh = () => {
    setDrift(null);
    void queryClient.invalidateQueries({ queryKey: [ORG_REQUESTS_QUERY_KEY, config.orgRef] });
  };

  const confirm = useMutation({
    mutationFn: async () => {
      if (shown === null) throw new Error('nothing to confirm');
      return decodeOrgRequestDetail(
        await gate.run(() => confirmOrgRequest(config.orgRef, requestId, shown.contentHash)),
      );
    },
    onMutate: () => {
      setActionError(null);
      setMaybeCharged(null);
      firstPasskey.reset();
    },
    onSuccess: (after) => {
      if (after === null || drawn === null) return refresh();
      if (after.state === 'completed' || after.state === 'completion_failed') {
        setOutcome({ kind: 'completed', drawn, detail: after });
      } else if (after.state === 'awaiting_second_confirmer') {
        setOutcome({ kind: 'first_of_two', detail: after });
      }
      void queryClient.invalidateQueries({ queryKey: [ORG_REQUESTS_QUERY_KEY, config.orgRef] });
    },
    onError: (error) => {
      if (isStepUpCancelled(error)) return;
      if (firstPasskey.caught(error)) return;
      const changed = orgRequestChangedTo(error);
      if (changed !== null && drawn !== null && shown !== null) {
        setDrift({
          render: changed.render,
          contentHash: changed.contentHash,
          changed: { keys: changedFieldKeys(shown.render, changed.render), before: drawn },
        });
        return;
      }
      const charged = orgRequestMaybeCharged(error);
      if (charged !== null) {
        setMaybeCharged(charged.detail);
        return;
      }
      if (isOrgRequestNeedsAnotherPerson(error)) {
        setNeedsAnother(true);
        return;
      }
      if (isOrgRequestNotPending(error)) {
        setActionError(REQUEST_PAGE_COPY.endedUnderYou);
        refresh();
        return;
      }
      if (isOrgRequestIneligible(error)) {
        refresh();
        return;
      }
      // An unknown failure (a dropped connection, an edge 5xx) may still have completed it, so
      // the page reads the request again and draws what is true, and the line claims nothing
      // about what happened (review-74's F7).
      setActionError(REQUEST_PAGE_COPY.confirmFailed);
      refresh();
    },
  });

  const decline = useMutation({
    mutationFn: async () =>
      decodeOrgRequestDetail(await declineOrgRequest(config.orgRef, requestId)),
    onMutate: () => setActionError(null),
    onSuccess: (after) => {
      if (after !== null) setOutcome({ kind: 'declined', drawn, detail: after });
      void queryClient.invalidateQueries({ queryKey: [ORG_REQUESTS_QUERY_KEY, config.orgRef] });
    },
    onError: (error) => {
      if (isOrgRequestNotPending(error)) {
        setActionError(REQUEST_PAGE_COPY.endedUnderYou);
        refresh();
        return;
      }
      // MCP-P5l: a charge may stand, so it cannot end; the read then draws that state.
      const settling = orgRequestPaymentSettling(error);
      if (settling !== null) {
        setActionError(settling.detail);
        refresh();
        return;
      }
      setActionError(REQUEST_PAGE_COPY.declineFailed);
    },
  });

  // MCP-P6a's B6: the person an agent asked for, or who proposed it, takes it back. The page then
  // reads the request as it ended (its kept render, read-only).
  const withdraw = useMutation({
    mutationFn: async () =>
      decodeOrgRequestDetail(await withdrawOrgRequest(config.orgRef, requestId)),
    onMutate: () => setActionError(null),
    onSuccess: () => refresh(),
    onError: (error) => {
      if (isOrgRequestNotPending(error)) {
        setActionError(REQUEST_PAGE_COPY.endedUnderYou);
        refresh();
        return;
      }
      const settling = orgRequestPaymentSettling(error);
      if (settling !== null) {
        setActionError(settling.detail);
        refresh();
        return;
      }
      setActionError(REQUEST_PAGE_COPY.withdrawFailed);
    },
  });

  const back = (
    <a
      href={inboxHref}
      onClick={(event) => {
        if (!shouldInterceptClick(event)) return;
        event.preventDefault();
        onNavigate(inboxHref);
      }}
      className="inline-flex items-center gap-6 self-start font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none hover:underline focus-visible:underline"
      {...testIdProp(`${PREFIX}-back`)}
    >
      <Icon icon={ArrowLeft} size={14} aria-hidden />
      {REQUEST_PAGE_COPY.breadcrumb}
    </a>
  );

  // ── Loading, absent, failed ───────────────────────────────────────────────
  if (detailQuery.isPending) {
    return (
      <PageFrame testId="page-org-request" width="focus">
        {back}
        <Skeleton className="h-[32px] w-2/3" />
        <Skeleton className="h-[280px] w-full" />
      </PageFrame>
    );
  }
  if (detailQuery.isError || detail === null) {
    const absent = isOrgConsoleUnavailable(detailQuery.error);
    return (
      <PageFrame testId="page-org-request" width="focus">
        {back}
        <Card>
          <CardBody className="flex flex-col items-start gap-8">
            <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
              {absent ? REQUEST_PAGE_COPY.notFoundTitle : REQUEST_PAGE_COPY.loadError}
            </h2>
            {absent ? (
              <p className="font-sans text-s14 leading-h15 tracking-givmo text-text-secondary">
                {REQUEST_PAGE_COPY.notFoundDescription}
              </p>
            ) : (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => void detailQuery.refetch()}
              >
                {REQUEST_PAGE_COPY.retry}
              </Button>
            )}
          </CardBody>
        </Card>
      </PageFrame>
    );
  }

  // ── What happened ─────────────────────────────────────────────────────────
  const proposer = proposedByOf(shown?.render ?? detail.render ?? detail.preview);
  const clientName = proposer.clientName ?? PROPOSER_COPY.unnamedAgent;
  // The same agent mid-sentence: one Givmo can't name is lowercase there, never read as a name.
  const clientNameMidSentence = proposer.clientName ?? PROPOSER_COPY.unnamedAgentMidSentence;
  const byPerson = detail.proposer.kind === 'human_session';
  const asker = byPerson ? (proposer.person ?? PROPOSER_COPY.unnamedPerson) : clientName;

  if (outcome !== null && outcome.kind !== 'first_of_two') {
    return (
      <PageFrame testId="page-org-request" width="focus">
        {back}
        <OutcomePanel
          outcome={outcome}
          client={byPerson ? null : { name: clientName, nameMidSentence: clientNameMidSentence }}
          orgNoun={config.vocab.orgNoun}
          actHref={actHref}
          inboxHref={inboxHref}
          onNavigate={onNavigate}
        />
      </PageFrame>
    );
  }

  const open = isOpenRequestState(detail.state) && shown !== null && drawn !== null;
  if (!open || drawn === null) {
    // An ended request's render is the one it ended with (B7), drawn read-only beneath its state;
    // a backend without it answers none, and the page says only the state and when.
    const kept = detail.render !== null ? drawRequest(detail.render, words) : null;
    return (
      <PageFrame testId="page-org-request" width="focus">
        {back}
        <PageHeader
          title={kept?.title ?? REQUEST_PAGE_COPY.endedTitle}
          subtitle={
            <span className="inline-flex flex-wrap items-center gap-8">
              <Badge tone={requestStateTone(detail.state)} size="sm" testId={`${PREFIX}-state`}>
                {requestStateLabel(detail.state)}
              </Badge>
              <span>{formatRequestTime(detail.endedAt ?? detail.expiresAt)}</span>
            </span>
          }
          organizationName={config.vocab.orgName}
          organizationNameTestId={`${PREFIX}-org-badge`}
        />
        <EndedPanel
          detail={detail}
          clientName={clientName}
          personName={proposer.person ?? PROPOSER_COPY.unnamedPerson}
        />
        {kept !== null && (
          <Card testId={`${PREFIX}-card`}>
            <CardBody className="flex flex-col gap-20">
              <h2 className="font-sans text-s14 font-w600 tracking-givmo text-text-secondary">
                {byPerson
                  ? REQUEST_PAGE_COPY.whatHeadingPerson(asker)
                  : REQUEST_PAGE_COPY.whatHeadingEnded(clientNameMidSentence)}
              </h2>
              <Hero drawn={kept} changed={null} viewerConfirms={false} />
              {kept.explanation !== null && (
                <p className="-mt-6 font-sans text-s13 leading-h15 tracking-normal text-text-secondary">
                  <span className="font-w600 text-text-strong">
                    {kept.outcome.limit}
                    {'. '}
                  </span>
                  {kept.explanation}
                </p>
              )}
              {kept.kind === 'money' && (
                <dl className="flex flex-col rounded-lg border border-border-subtle">
                  {kept.rows.map((row, index) => (
                    <Row
                      key={row.key}
                      row={row}
                      first={index === 0}
                      changed={false}
                      before={null}
                    />
                  ))}
                </dl>
              )}
            </CardBody>
          </Card>
        )}
      </PageFrame>
    );
  }

  // ── Open: draw it ─────────────────────────────────────────────────────────
  const standing =
    outcome?.kind === 'first_of_two'
      ? 'confirmed_mine'
      : needsAnother
        ? 'needs_another_person'
        : viewerStanding(
            drift !== null ? { ...detail, render: drift.render } : detail,
            config.selfUserId ?? null,
          );
  const eligible = shown!.render.eligible;
  const confirmations =
    outcome?.kind === 'first_of_two' ? outcome.detail.confirmations : detail.confirmations;
  const required =
    outcome?.kind === 'first_of_two'
      ? outcome.detail.requiredConfirmers
      : detail.requiredConfirmers;
  const confirmOff =
    !drawn.complete || !eligible || standing !== 'can_confirm' || firstPasskey.shut;
  const busy = confirm.isPending || decline.isPending || withdraw.isPending;
  // The estate's maybe-charged words: from the confirm's own 503, or from the read (MCP-P5l), so a
  // page drawn fresh after that answer still says it.
  const charged = maybeCharged ?? detail.maybeCharged?.detail ?? null;
  const noDecline = detail.maybeCharged?.declineDetail || BANNER_COPY.maybeChargedNoDecline;
  const mayWithdraw = detail.allowedActions.includes('withdraw');
  const others = confirmations.filter((entry) => entry.userId !== (config.selfUserId ?? ''));
  // The viewer is the person the agent acts for, or the person who proposed the change.
  const isMine =
    config.selfUserId != null && detail.proposer.delegatingUserId === config.selfUserId;
  // The buttons follow what the server offers this person (`allowed_actions`; review-74's F4 and
  // F5). Confirm, on or off, for someone who may confirm or whom dual control does not count;
  // Decline beside it, but never for the person who proposed the change themselves, who takes it
  // back instead (MCP-P5g), and wherever it is offered (a first approver who changes their mind);
  // Withdraw wherever it is offered, and, off, beside a charge that may stand for the person who
  // could otherwise take it back.
  const mayDecline = detail.allowedActions.includes('decline');
  const confirmable = standing === 'can_confirm' || standing === 'needs_another_person';
  const showDecline = mayDecline || (confirmable && !(byPerson && isMine));
  const showWithdraw = mayWithdraw || (charged !== null && isMine);

  return (
    <PageFrame testId="page-org-request" width="focus">
      {back}
      <PageHeader
        title={drawn.title}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-8">
            <Badge tone={requestStateTone(detail.state)} size="sm" testId={`${PREFIX}-state`}>
              {requestStateLabel(detail.state)}
            </Badge>
            <span>
              {(byPerson ? PROPOSER_COPY.proposed : PROPOSER_COPY.asked)(
                relativeTime(detail.createdAt),
              )}{' '}
              · {PROPOSER_COPY.expires(relativeTime(detail.expiresAt))}
            </span>
          </span>
        }
        organizationName={config.vocab.orgName}
        organizationNameTestId={`${PREFIX}-org-badge`}
      />

      {charged !== null && (
        <Callout
          tone="danger"
          icon={TriangleAlert}
          title={BANNER_COPY.maybeChargedTitle}
          testId={`${PREFIX}-maybe-charged`}
        >
          <p>{charged}</p>
          <p>{BANNER_COPY.maybeChargedHint}</p>
        </Callout>
      )}
      {shown!.changed !== null && (
        <Callout
          tone="warning"
          icon={TriangleAlert}
          title={BANNER_COPY.changedTitle}
          testId={`${PREFIX}-changed`}
        >
          <p>{BANNER_COPY.changedBody}</p>
        </Callout>
      )}
      {!drawn.complete && (
        <Callout
          tone="warning"
          icon={TriangleAlert}
          title={BANNER_COPY.undrawableTitle}
          testId={`${PREFIX}-undrawable`}
        >
          <p>{BANNER_COPY.undrawableBody}</p>
        </Callout>
      )}
      {drawn.complete && !eligible && (
        <Callout
          tone="warning"
          icon={TriangleAlert}
          title={BANNER_COPY.ineligibleTitle}
          testId={`${PREFIX}-ineligible`}
        >
          <p>{BANNER_COPY.ineligibleBody(formatRequestTime(detail.expiresAt))}</p>
        </Callout>
      )}
      {standing === 'confirmed_mine' && (
        // The person who proposed the change gave its first approval (06a's approved words); a
        // first approver of an AGENT's request only approved it (review-74's F4).
        <Callout
          tone="info"
          icon={Info}
          title={byPerson ? BANNER_COPY.secondPersonMineTitle : BANNER_COPY.approvedFirstTitle}
          testId={`${PREFIX}-second-mine`}
        >
          <p>{byPerson ? BANNER_COPY.secondPersonMineBody : BANNER_COPY.approvedFirstBody}</p>
        </Callout>
      )}
      {standing === 'needs_another_person' && (
        <Callout
          tone="info"
          icon={Info}
          title={BANNER_COPY.delegatorNotCountedTitle}
          testId={`${PREFIX}-needs-another`}
        >
          <p>{BANNER_COPY.delegatorNotCountedBody(clientNameMidSentence)}</p>
        </Callout>
      )}
      {standing === 'view_only' && (
        <Callout
          tone="info"
          icon={Info}
          title={BANNER_COPY.viewOnlyTitle}
          testId={`${PREFIX}-view-only`}
        >
          <p>{BANNER_COPY.viewOnlyBody(formatRequestTime(detail.expiresAt))}</p>
        </Callout>
      )}
      {standing === 'can_confirm' && others.length > 0 && required > confirmations.length && (
        <Callout
          tone="info"
          icon={Info}
          title={BANNER_COPY.secondPersonOtherTitle}
          testId={`${PREFIX}-second-other`}
        >
          <p>
            {BANNER_COPY.secondPersonOtherBody(
              others[0]?.name ?? PROPOSER_COPY.unnamedPerson,
              formatRequestTime(others[0]?.at ?? null),
            )}
          </p>
        </Callout>
      )}

      <Card testId={`${PREFIX}-card`}>
        <CardBody className="flex flex-col gap-20">
          <h2 className="font-sans text-s14 font-w600 tracking-givmo text-text-secondary">
            {byPerson
              ? isMine
                ? REQUEST_PAGE_COPY.whatHeadingMine
                : REQUEST_PAGE_COPY.whatHeadingPerson(asker)
              : REQUEST_PAGE_COPY.whatHeading(clientNameMidSentence)}
          </h2>

          <Hero
            drawn={drawn}
            changed={shown!.changed}
            viewerConfirms={standing === 'can_confirm'}
          />

          {drawn.explanation !== null && (
            <p
              className="-mt-6 font-sans text-s13 leading-h15 tracking-normal text-text-secondary"
              {...testIdProp(`${PREFIX}-explanation`)}
            >
              <span className="font-w600 text-text-strong">
                {drawn.outcome.limit}
                {'. '}
              </span>
              {drawn.explanation}
            </p>
          )}

          {(() => {
            const visible = drawn.rows.filter(
              (row) =>
                !(
                  drawn.kind === 'floor' &&
                  row.drawn &&
                  ['floor_kind', 'from', 'to'].includes(row.key)
                ),
            );
            if (visible.length === 0 && !(drawn.kind === 'money' && detail.why.length > 0))
              return null;
            return (
              <dl className="flex flex-col rounded-lg border border-border-subtle">
                {visible.map((row, index) => (
                  <Row
                    key={row.key}
                    row={row}
                    first={index === 0}
                    changed={shown!.changed?.keys.includes(row.key) === true}
                    before={
                      shown!.changed?.before.rows.find((old) => old.key === row.key)?.value ?? null
                    }
                  />
                ))}
                {drawn.kind === 'money' && detail.why.length > 0 && (
                  <WhyRow detail={detail} words={words} />
                )}
              </dl>
            );
          })()}

          {drawn.agentText !== null && (
            <div className="flex flex-col gap-6" {...testIdProp(`${PREFIX}-agent-text`)}>
              <p className="font-sans text-s12 font-w600 tracking-normal text-text-secondary">
                {ACT_ROW_COPY.agentMessage}
              </p>
              <p className="rounded-lg border border-dashed border-border-strong px-12 py-10 font-sans text-s13 leading-h15 tracking-normal text-text-secondary">
                {drawn.agentText}
              </p>
            </div>
          )}

          {drawn.disclosure !== null && (
            <DisclosureTerms
              heading={ACT_ROW_COPY.terms}
              sections={[
                {
                  key: drawn.disclosure.versionId || 'terms',
                  label: null,
                  lines: [drawn.disclosure.body],
                },
              ]}
              appearance="saas"
              headingLevel={3}
              testId={`${PREFIX}-terms`}
            />
          )}
        </CardBody>
      </Card>

      <Card testId={`${PREFIX}-who`}>
        <CardBody className="flex flex-col gap-12">
          <h2 className="font-sans text-s14 font-w600 tracking-givmo text-text-secondary">
            {byPerson ? PROPOSER_COPY.whoProposedHeading : PROPOSER_COPY.whoAskedHeading}
          </h2>
          <div className="flex items-start gap-12">
            <span
              className="flex size-36 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600"
              aria-hidden
            >
              <Icon icon={byPerson ? UserRound : Bot} size={18} />
            </span>
            <div className="flex min-w-0 flex-col gap-2">
              <p
                className="font-sans text-s14 font-w600 tracking-givmo text-text-strong"
                {...testIdProp(`${PREFIX}-who-name`)}
              >
                {byPerson ? (isMine ? PROPOSER_COPY.you : asker) : clientName}
              </p>
              <p className="font-sans text-s13 tracking-givmo text-text-tertiary">
                {byPerson
                  ? PROPOSER_COPY.personWhere
                  : isMine
                    ? PROPOSER_COPY.actingAsYou
                    : proposer.person !== null
                      ? PROPOSER_COPY.actingAs(proposer.person)
                      : ''}
              </p>
              {!byPerson && (isMine || proposer.person !== null) && (
                <p className="mt-4 font-sans text-s12 leading-h15 tracking-normal text-text-tertiary">
                  {isMine
                    ? PROPOSER_COPY.agentAuthorityYours
                    : PROPOSER_COPY.agentAuthority(proposer.person ?? '')}
                </p>
              )}
            </div>
          </div>
          <dl className="flex flex-col gap-6 border-t border-border-subtle pt-12">
            <TimeRow
              label={byPerson ? PROPOSER_COPY.proposedLabel : PROPOSER_COPY.askedLabel}
              value={formatRequestTime(detail.createdAt)}
            />
            <TimeRow
              label={PROPOSER_COPY.expiresLabel}
              value={formatRequestTime(detail.expiresAt)}
              hint={PROPOSER_COPY.expiresHint}
            />
            {required > 1 && (
              <TimeRow
                label={BANNER_COPY.approvalsLabel(confirmations.length, required)}
                value={confirmations
                  .map((entry, index) => {
                    // Only the first name opens the line; an unnamed person after it is lowercase.
                    const name =
                      entry.name ??
                      (index === 0
                        ? PROPOSER_COPY.unnamedPerson
                        : PROPOSER_COPY.unnamedPersonMidSentence);
                    return entry.userId === config.selfUserId
                      ? `${name} (${BANNER_COPY.you})`
                      : name;
                  })
                  .join(', ')}
              />
            )}
          </dl>
        </CardBody>
      </Card>

      {actionError !== null && (
        <ErrorMessage className="pt-0" testId={`${PREFIX}-action-error`}>
          {actionError}
        </ErrorMessage>
      )}

      {firstPasskey.shown && (
        <Callout
          tone="info"
          icon={Info}
          title={REQUEST_PAGE_COPY.needsPasskeyTitle}
          testId={`${PREFIX}-needs-passkey`}
        >
          <p>
            {firstPasskey.cantBeMade
              ? REQUEST_PAGE_COPY.passkeyCantBeMadeBody
              : firstPasskey.offer !== undefined
                ? REQUEST_PAGE_COPY.needsPasskeyOfferBody
                : REQUEST_PAGE_COPY.needsPasskeyBody}
          </p>
        </Callout>
      )}
      <div className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-actions`)}>
        {firstPasskey.offer !== undefined && (
          // In Confirm's place. Decline and Withdraw need no passkey, so they stay below it.
          <div className="flex justify-end">{firstPasskey.offer}</div>
        )}
        <div className="flex flex-col-reverse gap-8 sm:flex-row sm:justify-end">
          {showWithdraw && (
            // The person who proposed it, or whose agent asked, takes it back (B6).
            <Button
              appearance="saas"
              tone="secondary"
              size="lg"
              disabled={busy || charged !== null}
              loading={withdraw.isPending}
              onClick={() => withdraw.mutate()}
              testId={`${PREFIX}-withdraw`}
            >
              {REQUEST_PAGE_COPY.withdraw}
            </Button>
          )}
          {showDecline && (
            <Button
              appearance="saas"
              tone="secondary"
              size="lg"
              disabled={busy || charged !== null || !mayDecline}
              loading={decline.isPending}
              onClick={() => decline.mutate()}
              testId={`${PREFIX}-decline`}
            >
              {REQUEST_PAGE_COPY.decline}
            </Button>
          )}
          {confirmable && firstPasskey.waiting && (
            // After "Not now" a press of Confirm could only be refused again. This asks the server
            // nothing and can confirm nothing; a passkey set up brings Confirm back.
            <Button
              appearance="saas"
              tone="primary"
              size="lg"
              disabled={busy}
              onClick={firstPasskey.reopen}
              testId={`${PREFIX}-set-up-passkey`}
            >
              {REQUEST_PAGE_COPY.setUpPasskey}
            </Button>
          )}
          {confirmable && firstPasskey.offer === undefined && !firstPasskey.waiting && (
            <Button
              appearance="saas"
              tone="primary"
              size="lg"
              disabled={busy || confirmOff}
              loading={confirm.isPending && !gate.prompting}
              onClick={() => confirm.mutate()}
              testId={`${PREFIX}-confirm`}
            >
              {charged !== null ? BANNER_COPY.tryAgain : REQUEST_PAGE_COPY.confirm}
            </Button>
          )}
        </div>
        {charged !== null ? (
          <p
            className="text-right font-sans text-s12 tracking-normal text-text-tertiary max-sm:text-left"
            {...testIdProp(`${PREFIX}-no-decline`)}
          >
            {showDecline || !showWithdraw
              ? noDecline
              : detail.maybeCharged?.withdrawDetail || noDecline}
          </p>
        ) : !drawn.complete ? (
          <p
            className="text-right font-sans text-s12 tracking-normal text-text-tertiary max-sm:text-left"
            {...testIdProp(`${PREFIX}-confirm-off`)}
          >
            {BANNER_COPY.undrawableReason}
          </p>
        ) : !firstPasskey.shown &&
          standing === 'can_confirm' &&
          eligible &&
          detail.stepUp !== null &&
          !detail.stepUp.satisfied ? (
          <p
            className="text-right font-sans text-s12 tracking-normal text-text-tertiary max-sm:text-left"
            {...testIdProp(`${PREFIX}-step-up-hint`)}
          >
            {REQUEST_PAGE_COPY.stepUpHint}
          </p>
        ) : null}
      </div>
      {gate.dialog}
    </PageFrame>
  );
}

// ── Parts ───────────────────────────────────────────────────────────────────

const CALLOUT_TONE = {
  info: { box: 'border-brand-100 bg-brand-50', icon: 'text-brand-600' },
  warning: { box: 'border-warning-100 bg-warning-50', icon: 'text-warning-600' },
  danger: { box: 'border-danger-100 bg-danger-50', icon: 'text-danger-600' },
} as const;

function Callout({
  tone,
  icon,
  title,
  testId,
  children,
}: {
  tone: keyof typeof CALLOUT_TONE;
  icon: LucideLikeIcon;
  title: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <div
      role={tone === 'info' ? 'status' : 'alert'}
      className={`flex items-start gap-12 rounded-lg border px-16 py-14 ${CALLOUT_TONE[tone].box}`}
      {...testIdProp(testId)}
    >
      <span className={`mt-2 shrink-0 ${CALLOUT_TONE[tone].icon}`}>
        <Icon icon={icon} size={18} aria-hidden />
      </span>
      <div className="flex min-w-0 flex-col gap-4">
        <p className="font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong">
          {title}
        </p>
        <div className="flex flex-col gap-4 font-sans text-s13 leading-h15 tracking-normal text-text-secondary">
          {children}
        </div>
      </div>
    </div>
  );
}

/** The change at a glance: the limit from → to, or the gift's amount and recipient. */
function Hero({
  drawn,
  changed,
  viewerConfirms,
}: {
  drawn: DrawnRequest;
  changed: Shown['changed'];
  viewerConfirms: boolean;
}) {
  if (drawn.hero === null) return null;
  if (drawn.kind === 'money') {
    return (
      <div
        className="flex flex-col items-center gap-8 py-4 text-center"
        {...testIdProp(`${PREFIX}-hero`)}
      >
        {drawn.hero.recipient !== null && (
          <Avatar
            name={drawn.hero.recipient}
            src={null}
            size="lg"
            className="border border-border-subtle"
          />
        )}
        <p className="font-sans text-s32 font-w700 leading-h12 tracking-givmo text-text-strong tabular-nums">
          {drawn.hero.to}
        </p>
        {drawn.hero.recipient !== null && (
          <p className="font-sans text-s15 leading-h15 tracking-givmo text-text-secondary">
            to <span className="font-w600 text-text-strong">{drawn.hero.recipient}</span>
          </p>
        )}
      </div>
    );
  }
  const fromChanged = changed?.keys.includes('from') === true;
  const toChanged = changed?.keys.includes('to') === true;
  const before = changed?.before.hero ?? null;
  return (
    <div
      className="grid grid-cols-1 items-stretch gap-6 sm:grid-cols-[1fr_auto_1fr] sm:gap-8"
      {...testIdProp(`${PREFIX}-hero`)}
    >
      <LimitBox
        label={ACT_ROW_COPY.now}
        value={drawn.hero.from ?? '—'}
        was={fromChanged ? (before?.from ?? null) : null}
      />
      <span className="flex items-center justify-center text-text-tertiary" aria-hidden>
        <Icon icon={ArrowRight} size={18} className="rotate-90 sm:rotate-0" />
      </span>
      <LimitBox
        label={viewerConfirms ? ACT_ROW_COPY.ifConfirmed : ACT_ROW_COPY.ifConfirmedByOthers}
        value={drawn.hero.to}
        was={toChanged ? (before?.to ?? null) : null}
        strong
      />
    </div>
  );
}

function LimitBox({
  label,
  value,
  was,
  strong = false,
}: {
  label: string;
  value: string;
  was: string | null;
  strong?: boolean;
}) {
  return (
    <div
      className={`flex min-w-0 flex-col justify-between gap-4 rounded-lg border px-14 py-12 ${
        was !== null
          ? 'border-warning-500 bg-warning-50'
          : strong
            ? 'border-brand-200 bg-brand-50'
            : 'border-border-subtle bg-surface-sunken'
      }`}
    >
      <span className="flex flex-wrap items-center gap-6 font-sans text-s12 font-w600 tracking-normal text-text-tertiary">
        {label}
        {was !== null && (
          <Badge tone="warning" size="sm">
            {BANNER_COPY.changedBadge}
          </Badge>
        )}
      </span>
      {was !== null && (
        <span className="font-sans text-s13 tracking-givmo text-text-tertiary line-through tabular-nums">
          {was}
        </span>
      )}
      <span
        className={`whitespace-nowrap font-sans font-w700 tracking-givmo tabular-nums ${
          strong ? 'text-s22 text-text-strong' : 'text-s22 text-text-secondary'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function Row({
  row,
  first,
  changed,
  before,
}: {
  row: DrawnRow;
  first: boolean;
  changed: boolean;
  before: string | null;
}) {
  if (!row.drawn) {
    return (
      <div className={`px-12 py-10 ${first ? '' : 'border-t border-border-subtle'}`}>
        <p
          className="rounded-md border border-dashed border-border-strong bg-surface-sunken px-12 py-10 font-sans text-s13 tracking-normal text-text-tertiary"
          {...testIdProp(`${PREFIX}-undrawable-field`)}
        >
          {BANNER_COPY.undrawableField}
        </p>
      </div>
    );
  }
  return (
    <div
      className={`flex flex-col gap-4 px-12 py-10 sm:flex-row sm:items-baseline sm:justify-between sm:gap-16 ${
        first ? '' : 'border-t border-border-subtle'
      } ${changed ? 'bg-warning-50' : ''}`}
    >
      <dt className="shrink-0 font-sans text-s13 tracking-givmo text-text-tertiary">{row.label}</dt>
      <dd className="min-w-0 font-sans text-s13 font-w500 leading-h15 tracking-givmo text-text-strong sm:text-right">
        {changed && before !== null && (
          <span className="mr-6 font-w400 text-text-tertiary line-through">{before}</span>
        )}
        {row.value}
      </dd>
    </div>
  );
}

function WhyRow({ detail, words: host }: { detail: OrgRequestDetail; words: OrgRequestWords }) {
  const family = detail.kind.startsWith('money_act.') ? detail.kind.slice('money_act.'.length) : '';
  const words = detail.why
    .map((code) =>
      code === 'family_not_enabled'
        ? (host.familyOff[family] ?? WHY_COPY[code])
        : code === 'recipient_not_allowlisted'
          ? host.notAllowlisted
          : WHY_COPY[code],
    )
    .filter((line): line is string => !!line);
  const period = detail.context.find((entry) => entry.breach === 'period_max_exceeded');
  return (
    <div className="flex flex-col gap-4 border-t border-border-subtle px-12 py-10 sm:flex-row sm:items-baseline sm:justify-between sm:gap-16">
      <dt className="shrink-0 font-sans text-s13 tracking-givmo text-text-tertiary">
        {ACT_ROW_COPY.why}
      </dt>
      <dd className="min-w-0 font-sans text-s13 font-w500 leading-h15 tracking-givmo text-text-strong sm:text-right">
        {words.join(' ')}
        {period !== undefined && period.limitMinor !== null && period.usedMinor !== null && (
          <span className="block font-w400 text-text-tertiary">
            {FLOOR_CONTEXT_COPY.periodMax(
              `${formatMinorUsd(period.limitMinor)} ${ACT_ROW_COPY.perDay}`,
              formatMinorUsd(period.usedMinor),
            )}
          </span>
        )}
      </dd>
    </div>
  );
}

function TimeRow({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-16">
      <dt className="shrink-0 font-sans text-s13 tracking-givmo text-text-tertiary">{label}</dt>
      <dd className="min-w-0 font-sans text-s13 font-w500 tracking-givmo text-text-strong sm:text-right">
        {value}
        {hint !== undefined && (
          <span className="block font-sans text-s12 font-w400 tracking-normal text-text-tertiary">
            {hint}
          </span>
        )}
      </dd>
    </div>
  );
}

function OutcomePanel({
  outcome,
  client,
  orgNoun,
  actHref,
  inboxHref,
  onNavigate,
}: {
  outcome: Extract<Outcome, { kind: 'completed' | 'declined' }>;
  /** The agent that asked, opening a sentence and within one; `null` for a person's own change. */
  client: { readonly name: string; readonly nameMidSentence: string } | null;
  orgNoun: string;
  actHref?: (actRef: string) => string | null;
  inboxHref: string;
  onNavigate: (href: string) => void;
}) {
  const failed = outcome.kind === 'completed' && outcome.detail.state === 'completion_failed';
  const done = outcome.kind === 'completed' && !failed;
  const drawn = outcome.drawn;
  const line = !done
    ? failed
      ? OUTCOME_COPY.failedLine
      : OUTCOME_COPY.declinedBody
    : drawn?.kind === 'money'
      ? OUTCOME_COPY.giftDone(
          orgNoun,
          drawn.outcome.amount ?? '',
          drawn.outcome.recipient ?? '',
          drawn.outcome.source ?? '',
        )
      : OUTCOME_COPY.floorDone(drawn?.outcome.limit ?? '', drawn?.outcome.value ?? '');
  const act =
    done && drawn?.kind === 'money' && outcome.detail.actRef && actHref
      ? actHref(outcome.detail.actRef)
      : null;
  return (
    <Card testId={`${PREFIX}-outcome`}>
      <CardBody className="flex flex-col items-center gap-12 py-36 text-center">
        <span
          className={`flex size-48 items-center justify-center rounded-full ${
            done ? 'bg-success-50 text-success-600' : 'bg-neutral-100 text-text-tertiary'
          }`}
          aria-hidden
        >
          <Icon icon={done ? CircleCheck : Info} size={24} />
        </span>
        <h1 className="font-sans text-s22 font-w700 tracking-givmo text-text-strong">
          {done
            ? OUTCOME_COPY.doneTitle
            : failed
              ? requestStateLabel('completion_failed')
              : OUTCOME_COPY.declinedTitle}
        </h1>
        <p
          className="max-w-[460px] font-sans text-s15 leading-h15 tracking-givmo text-text-secondary"
          {...testIdProp(`${PREFIX}-outcome-line`)}
        >
          {line}
        </p>
        {done && outcome.detail.confirmations.length > 0 && (
          <p
            className="font-sans text-s13 tracking-normal text-text-tertiary"
            {...testIdProp(`${PREFIX}-outcome-who`)}
          >
            {OUTCOME_COPY.confirmedLine(
              outcome.detail.confirmations
                .map((entry) => entry.name ?? PROPOSER_COPY.unnamedPersonMidSentence)
                .join(' and '),
              formatRequestTime(outcome.detail.endedAt),
            )}
          </p>
        )}
        {client !== null && (
          <p className="max-w-[460px] font-sans text-s13 leading-h15 tracking-normal text-text-tertiary">
            {done
              ? OUTCOME_COPY.backToChat(client.nameMidSentence)
              : OUTCOME_COPY.declinedChat(client.name)}
          </p>
        )}
        <div className="mt-8 flex flex-col gap-8 sm:flex-row">
          {act !== null && (
            <Button appearance="saas" tone="secondary" size="md" onClick={() => onNavigate(act)}>
              {OUTCOME_COPY.seeInDonations}
            </Button>
          )}
          <Button
            appearance="saas"
            tone={act !== null ? 'secondary' : 'primary'}
            size="md"
            onClick={() => onNavigate(inboxHref)}
            testId={`${PREFIX}-outcome-back`}
          >
            {REQUEST_PAGE_COPY.backToRequests}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

/** A request that ended before this visit: its state and why, from the stored facts alone. */
function EndedPanel({
  detail,
  clientName,
  personName,
}: {
  detail: OrgRequestDetail;
  clientName: string;
  /** The person the agent acted for, or who proposed the change. */
  personName: string;
}) {
  const when = formatRequestTime(detail.endedAt ?? detail.expiresAt);
  const voidLine =
    detail.state === 'void' && detail.voidReason !== null
      ? (VOID_REASON_COPY[detail.voidReason]?.(clientName, personName) ?? null)
      : null;
  const names = detail.confirmations
    .map((entry) => entry.name)
    .filter((name): name is string => !!name);
  const line =
    detail.state === 'expired'
      ? OUTCOME_COPY.expiredLine(when)
      : detail.state === 'completion_failed'
        ? OUTCOME_COPY.failedLine
        : detail.state === 'completed' && names.length > 0
          ? OUTCOME_COPY.confirmedBy(names.join(' and '), when)
          : voidLine;
  // Nothing beyond the header's state and time to say (the landed wire's confirmed request): no card.
  if (line === null) return null;
  return (
    <Card testId={`${PREFIX}-ended`}>
      <CardBody className="flex flex-col items-start gap-8">
        <p
          className="font-sans text-s14 leading-h15 tracking-givmo text-text-secondary"
          {...testIdProp(`${PREFIX}-ended-line`)}
        >
          {line}
        </p>
      </CardBody>
    </Card>
  );
}
